import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { getUser, State } from "app/reducers";
import {
  GroupSectionBaseAbstractComponent,
  GroupSectionStep,
  SelectButtonsData
} from "app/shared/modules/group-section/components/group-section-base.abstract.component";
import { AdditionalIncomeService } from "app/modules/statement/services/additional-income.service";
import {
  StepsControllerService,
  SubStepSaveDataInterface
} from "app/core/workflow/steps-controller/steps-controller.service";
import { FreelanceJobSubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/freelance-job.sub-steps";
import { FreelanceJobManager } from "app/modules/statement/models/additional-income/freelance-job.manager";
import { AutofocusHelper } from "app/core/helpers/autofocus.helper";
import { VatDeclarationSubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/vat-declaration.sub-steps";
import { VatDeclarationManager } from "app/modules/statement/models/additional-income/vat-declaration.manager";
import { User } from "app/users";

@Component({
  ...GroupSectionBaseAbstractComponent.metaData,
  selector: "app-additional-income-group-freelance",
  templateUrl: "./additional-income-group-freelance.component.html",
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false
})
export class AdditionalIncomeGroupFreelanceComponent extends GroupSectionBaseAbstractComponent implements OnInit {
  public groupTitle: string;
  public freelanceJobSteps = FreelanceJobSubSteps;
  public freelanceJobManager = FreelanceJobManager;
  public vatDeclarationManager = VatDeclarationManager;
  public isBusiness: boolean = false;
  private vatSelectedLocally = false;

  constructor(
    public additionalIncomeService: AdditionalIncomeService,
    public dialog: MatDialog,
    public store: Store<State>,
    public stepsControllerService: StepsControllerService
  ) {
    super(dialog, store, stepsControllerService);
  }

  ngOnInit() {
    this.groupTitle = this.title;
    this.init();

    this.store
      .select(getUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user: User) => {
        if (user) {
          this.isBusiness = user.isBusiness;
        }
      });

    this.stepsControllerService.reInitEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.init();
    });

    this.stepsControllerService.subStepSaveEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data: SubStepSaveDataInterface) => this.saveGroup(data)
    });

    super.ngOnInit();
  }

  /** =========================
   * SELECT BUTTON LOGIC
   * ========================= */
  public isSelected = (selectButtonName: string): boolean => {
    switch (selectButtonName) {
      case FreelanceJobSubSteps.FREELANCE:
        return !!FreelanceJobManager.getFreelance(this.additionalIncomeService.currentStatement).length;
      case FreelanceJobSubSteps.GEWERBE:
        return !!FreelanceJobManager.getGewerbe(this.additionalIncomeService.currentStatement).length;
      case FreelanceJobSubSteps.VOLUNTERING_INCOME:
        return !!FreelanceJobManager.getVolunteringIncome(this.additionalIncomeService.currentStatement).length;
      case FreelanceJobSubSteps.VAT_DECLARATION:
        if (this.vatSelectedLocally) {
          return true;
        }
        const vat = VatDeclarationManager.getVatDeclaration(this.additionalIncomeService.currentStatement)?.[0];
        return !!vat && !!(vat as any).declaration_type;
    }
    return false;
  };

  public displayWarning = (): boolean => {
    const isFreelanceSelected = this.isSelected(FreelanceJobSubSteps.FREELANCE);
    const isGewerbeSelected = this.isSelected(FreelanceJobSubSteps.GEWERBE);
    const isVatDeclarationSelected = this.isSelected(FreelanceJobSubSteps.VAT_DECLARATION);
    return (isFreelanceSelected || isGewerbeSelected) && !isVatDeclarationSelected;
  };

  public selectButtonClickAction = (selectButtonName: string): void => {
    switch (selectButtonName) {
      case FreelanceJobSubSteps.FREELANCE:
        if (!!FreelanceJobManager.getFreelance(this.additionalIncomeService.currentStatement).length) {
          this.showAreYouSurePopup((dialog) => {
            FreelanceJobManager.removeAllFreelance(this.additionalIncomeService.currentStatement);
            this.additionalIncomeService.updateAdditionalIncome(FreelanceJobSubSteps.FREELANCE, () => dialog.close());
          });
        } else {
          FreelanceJobManager.createFreelance(this.additionalIncomeService.currentStatement);
        }
        break;

      case FreelanceJobSubSteps.GEWERBE:
        if (!!FreelanceJobManager.getGewerbe(this.additionalIncomeService.currentStatement).length) {
          this.showAreYouSurePopup((dialog) => {
            FreelanceJobManager.removeAllGewerbe(this.additionalIncomeService.currentStatement);
            this.additionalIncomeService.updateAdditionalIncome(FreelanceJobSubSteps.GEWERBE, () => dialog.close());
          });
        } else {
          FreelanceJobManager.createGewerbe(this.additionalIncomeService.currentStatement);
        }
        break;

      case FreelanceJobSubSteps.VOLUNTERING_INCOME:
        if (!!FreelanceJobManager.getVolunteringIncome(this.additionalIncomeService.currentStatement).length) {
          this.showAreYouSurePopup((dialog) => {
            FreelanceJobManager.removeVolunteringIncome(this.additionalIncomeService.currentStatement);
            this.additionalIncomeService.updateAdditionalIncome(FreelanceJobSubSteps.VOLUNTERING_INCOME, () =>
              dialog.close()
            );
          });
        } else {
          FreelanceJobManager.createVolunteringIncome(this.additionalIncomeService.currentStatement);
        }
        break;

      case FreelanceJobSubSteps.VAT_DECLARATION:
        const vat = VatDeclarationManager.getVatDeclaration(this.additionalIncomeService.currentStatement)?.[0];
        const hasVatDeclaration = !!vat && !!(vat as any).declaration_type;

        if (hasVatDeclaration) {
          this.showAreYouSurePopup((dialog) => {
            VatDeclarationManager.removeVatDeclaration(this.additionalIncomeService.currentStatement);
            this.vatSelectedLocally = false;
            this.additionalIncomeService.updateAdditionalIncome(VatDeclarationSubSteps.VAT_DECLARATION, () =>
              dialog.close()
            );
          });
        } else {
          VatDeclarationManager.createVatDeclaration(this.additionalIncomeService.currentStatement);
          this.vatSelectedLocally = true;
        }
        break;
    }

    this.selectedButtonsChanged();
  };

  /** =========================
   * BUTTON DATA
   * ========================= */
  private setSelectButtonsData(): void {
    const statement = this.additionalIncomeService.getMainStatement();
    const currentYear = statement.fullYear;

    const selectButtonsData: SelectButtonsData[] = [
      {
        id: FreelanceJobSubSteps.FREELANCE,
        image: "assets/svg/additional_income/self_employment.svg",
        title:
          currentYear === 2020
            ? "ADDITIONAL_INCOME.FREELANCE_GROUP.FREELANCE.BTN_TITLE_PREMIUM"
            : "ADDITIONAL_INCOME.FREELANCE_GROUP.FREELANCE.BTN_TITLE"
      },
      {
        id: FreelanceJobSubSteps.GEWERBE,
        image: "assets/img/additional_income/gewerbe.png",
        title:
          currentYear === 2020
            ? "ADDITIONAL_INCOME.FREELANCE_GROUP.GEWERBE.BTN_TITLE_PREMIUM"
            : "ADDITIONAL_INCOME.FREELANCE_GROUP.GEWERBE.BTN_TITLE"
      },
      {
        id: FreelanceJobSubSteps.VAT_DECLARATION,
        image: "assets/img/additional_income/ikona-vat.svg",
        title: "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.BTN_TITLE"
      },
      {
        id: FreelanceJobSubSteps.VOLUNTERING_INCOME,
        image: "assets/svg/additional_income/voluntering.svg",
        title: "ADDITIONAL_INCOME.FREELANCE_GROUP.VOLUNTERING_INCOME.BTN_TITLE"
      }
    ];

    this.setSelectButtons(selectButtonsData);
  }

  /** =========================
   * SUBSTEPS
   * ========================= */
  private setSteps(): void {
    this.subSteps = [
      { id: FreelanceJobSubSteps.MAIN, isActive: true },
      { id: FreelanceJobSubSteps.FREELANCE, isActive: false },
      { id: FreelanceJobSubSteps.GEWERBE, isActive: false },
      { id: FreelanceJobSubSteps.VOLUNTERING_INCOME, isActive: false },
      { id: FreelanceJobSubSteps.VAT_DECLARATION, isActive: false }
    ];
  }

  /** =========================
   * SAVE GROUP
   * ========================= */
  private saveGroup({ index, callback }: SubStepSaveDataInterface): void {
    const currentStep = this.subSteps[index];

    if (currentStep && currentStep.id !== FreelanceJobSubSteps.MAIN) {
      this.additionalIncomeService.formValidationErrors(currentStep.id as FreelanceJobSubSteps);
      if (this.additionalIncomeService.epValidator.length) {
        callback(false);
        return;
      }
    }

    if (this.shouldSaveFreelanceJobsBeforeLeaving(currentStep, index)) {
      this.additionalIncomeService.loading = true;
      this.additionalIncomeService.updateAdditionalIncome(
        currentStep.id as FreelanceJobSubSteps,
        (success) => {
          if (!success) {
            callback(false);
            return;
          }

          callback(true);
          AutofocusHelper.autofocusInputMainContent();
        },
        true,
        false
      );
      return;
    }

    callback(true);
    AutofocusHelper.autofocusInputMainContent();
  }

  private shouldSaveFreelanceJobsBeforeLeaving(currentStep: GroupSectionStep, index: number): boolean {
    if (
      !currentStep ||
      ![FreelanceJobSubSteps.FREELANCE, FreelanceJobSubSteps.GEWERBE].includes(currentStep.id as FreelanceJobSubSteps)
    ) {
      return false;
    }

    const laterBusinessStep = this.subSteps
      .slice(index + 1)
      .find((step) =>
        [FreelanceJobSubSteps.FREELANCE, FreelanceJobSubSteps.GEWERBE].includes(step.id as FreelanceJobSubSteps)
      );

    return !laterBusinessStep || !this.isSelected(laterBusinessStep.id);
  }

  /** =========================
   * INIT
   * ========================= */
  private init(): void {
    this.setSteps();
    this.setSelectButtonsData();
    AutofocusHelper.autofocusInputMainContent();
  }

  public goToTheNextStep(): void {
    if (this.displayWarning()) return;
    super.goToTheNextStep();
  }

  protected readonly FreelanceJobSubSteps = FreelanceJobSubSteps;
}
